/**
 * Particle System
 *
 * Handles visual particle effects for skills, hits, deaths, etc.
 */

import { Vector2 } from '@shared/types';
import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { t } from '@locales/index';

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  sizeDecay: number;
  color: string;
  alpha: number;
  alphaDecay: number;
  gravity: number;
  friction: number;
  rotation: number;
  rotationSpeed: number;
  shape: 'circle' | 'square' | 'line' | 'star';
}

export interface ParticleEmitterConfig {
  position: Vector2;
  count: number;
  spread: number;        // Angle spread in radians
  direction: number;     // Base direction in radians
  speed: { min: number; max: number };
  life: { min: number; max: number };
  size: { min: number; max: number };
  sizeDecay?: number;
  color: string | string[];
  alpha?: number;
  alphaDecay?: number;
  gravity?: number;
  friction?: number;
  rotation?: boolean;
  shape?: 'circle' | 'square' | 'line' | 'star';
}

export interface FloatingText {
  x: number;
  y: number;
  vy: number;
  text: string;
  color: string;
  size: number;
  life: number;
  maxLife: number;
  critical?: boolean;
  bigDamage?: boolean;
  spawnTime?: number;
}

// Pre-computed constant
const TWO_PI = Math.PI * 2;

interface ChainLine {
  x1: number; y1: number;
  x2: number; y2: number;
  life: number;
  maxLife: number;
  color: string;
}

export class ParticleSystem {
  private particles: Particle[] = [];
  private floatingTexts: FloatingText[] = [];
  private chainLines: ChainLine[] = [];
  private readonly MAX_PARTICLES = 200;
  private readonly MAX_TEXTS = 50;

  // Reusable screen-space coords to avoid per-particle Vector2 allocation
  private _sx = 0;
  private _sy = 0;

  update(deltaTime: number): void {
    // Update particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];

      // Physics
      p.vx *= 1 - p.friction * deltaTime;
      p.vy *= 1 - p.friction * deltaTime;
      p.vy += p.gravity * deltaTime;

      p.x += p.vx * deltaTime;
      p.y += p.vy * deltaTime;

      // Decay
      p.life -= deltaTime;
      p.alpha -= p.alphaDecay * deltaTime;
      p.size -= p.sizeDecay * deltaTime;
      p.rotation += p.rotationSpeed * deltaTime;

      // Remove dead particles (swap-and-pop for O(1) removal)
      if (p.life <= 0 || p.alpha <= 0 || p.size <= 0) {
        this.particles[i] = this.particles[this.particles.length - 1];
        this.particles.pop();
      }
    }

    // Update floating texts
    for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
      const text = this.floatingTexts[i];

      text.y += text.vy * deltaTime;
      text.vy *= 0.95; // Slow down
      text.life -= deltaTime;

      if (text.life <= 0) {
        this.floatingTexts[i] = this.floatingTexts[this.floatingTexts.length - 1];
        this.floatingTexts.pop();
      }
    }

    // Update chain lines
    for (let i = this.chainLines.length - 1; i >= 0; i--) {
      this.chainLines[i].life -= deltaTime;
      if (this.chainLines[i].life <= 0) {
        this.chainLines[i] = this.chainLines[this.chainLines.length - 1];
        this.chainLines.pop();
      }
    }
  }

  emit(config: ParticleEmitterConfig): void {
    const count = Math.min(config.count, this.MAX_PARTICLES - this.particles.length);

    for (let i = 0; i < count; i++) {
      const angle = config.direction + (Math.random() - 0.5) * config.spread;
      const speed = this.randomRange(config.speed.min, config.speed.max);
      const life = this.randomRange(config.life.min, config.life.max);
      const size = this.randomRange(config.size.min, config.size.max);

      let color = config.color;
      if (Array.isArray(color)) {
        color = color[Math.floor(Math.random() * color.length)];
      }

      this.particles.push({
        x: config.position.x,
        y: config.position.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life,
        maxLife: life,
        size,
        sizeDecay: config.sizeDecay || 0,
        color,
        alpha: config.alpha || 1,
        alphaDecay: config.alphaDecay || (1 / life),
        gravity: config.gravity || 0,
        friction: config.friction || 0,
        rotation: 0,
        rotationSpeed: config.rotation ? (Math.random() - 0.5) * 10 : 0,
        shape: config.shape || 'circle',
      });
    }
  }

  // Preset emitters
  emitBurst(x: number, y: number, color: string = '#ffffff', count: number = 8): void {
    this.emit({
      position: { x, y },
      count,
      spread: TWO_PI,
      direction: -Math.PI / 2,
      speed: { min: 30, max: 80 },
      life: { min: 0.3, max: 0.6 },
      size: { min: 2, max: 4 },
      color,
      gravity: -20,
    });
  }

  emitHit(x: number, y: number, color: string = '#ffffff'): void {
    this.emit({
      position: { x, y },
      count: 8,
      spread: TWO_PI,
      direction: 0,
      speed: { min: 50, max: 150 },
      life: { min: 0.2, max: 0.4 },
      size: { min: 2, max: 5 },
      color,
      gravity: 100,
    });
  }

  emitBlood(x: number, y: number): void {
    this.emit({
      position: { x, y },
      count: 12,
      spread: Math.PI * 0.8,
      direction: -Math.PI / 2, // Upward
      speed: { min: 80, max: 200 },
      life: { min: 0.3, max: 0.6 },
      size: { min: 2, max: 6 },
      color: ['#8a1a1a', '#aa2a2a', '#cc3a3a'],
      gravity: 400,
    });
  }

  emitDeath(x: number, y: number, color: string = '#4a3a5a'): void {
    // Soul particles rising
    this.emit({
      position: { x, y },
      count: 20,
      spread: Math.PI / 2,
      direction: -Math.PI / 2,
      speed: { min: 30, max: 80 },
      life: { min: 0.8, max: 1.5 },
      size: { min: 4, max: 8 },
      sizeDecay: 3,
      color: [color, '#8888ff', '#aaaaff'],
      gravity: -50,
      alphaDecay: 0.8,
    });

    // Ground burst
    this.emit({
      position: { x, y },
      count: 15,
      spread: Math.PI,
      direction: -Math.PI / 2,
      speed: { min: 100, max: 250 },
      life: { min: 0.4, max: 0.8 },
      size: { min: 3, max: 7 },
      color: ['#333333', '#444444', color],
      gravity: 600,
    });
  }

  emitLevelUp(x: number, y: number): void {
    this.emit({
      position: { x, y },
      count: 30,
      spread: TWO_PI,
      direction: 0,
      speed: { min: 50, max: 150 },
      life: { min: 0.8, max: 1.5 },
      size: { min: 3, max: 8 },
      sizeDecay: 2,
      color: ['#ffd700', '#ffff44', '#ffffff'],
      gravity: -100,
      shape: 'star',
    });
  }

  emitMagic(x: number, y: number, color: string): void {
    this.emit({
      position: { x, y },
      count: 15,
      spread: TWO_PI,
      direction: 0,
      speed: { min: 20, max: 60 },
      life: { min: 0.5, max: 1.0 },
      size: { min: 2, max: 5 },
      color: [color, this.lightenColor(color, 30)],
      gravity: -30,
      alphaDecay: 1.5,
    });
  }

  emitFire(x: number, y: number): void {
    this.emit({
      position: { x, y },
      count: 10,
      spread: Math.PI / 3,
      direction: -Math.PI / 2,
      speed: { min: 30, max: 80 },
      life: { min: 0.3, max: 0.6 },
      size: { min: 4, max: 10 },
      sizeDecay: 8,
      color: ['#ff4400', '#ff8800', '#ffcc00', '#ffff44'],
      gravity: -200,
      alphaDecay: 2,
    });
  }

  emitIce(x: number, y: number): void {
    this.emit({
      position: { x, y },
      count: 12,
      spread: TWO_PI,
      direction: 0,
      speed: { min: 40, max: 100 },
      life: { min: 0.4, max: 0.8 },
      size: { min: 3, max: 6 },
      color: ['#aaffff', '#88ddff', '#ffffff'],
      gravity: 50,
      shape: 'square',
      rotation: true,
    });
  }

  emitLightning(x: number, y: number): void {
    this.emit({
      position: { x, y },
      count: 8,
      spread: TWO_PI,
      direction: 0,
      speed: { min: 100, max: 300 },
      life: { min: 0.1, max: 0.3 },
      size: { min: 2, max: 4 },
      color: ['#ffff44', '#ffffff', '#88ffff'],
      friction: 5,
      shape: 'line',
    });
  }

  emitChainLine(x1: number, y1: number, x2: number, y2: number, color: string = '#88ddff'): void {
    this.chainLines.push({ x1, y1, x2, y2, life: 0.3, maxLife: 0.3, color });
  }

  emitExplosion(x: number, y: number, radius: number = 50): void {
    // Core flash
    this.emit({
      position: { x, y },
      count: 20,
      spread: TWO_PI,
      direction: 0,
      speed: { min: radius, max: radius * 3 },
      life: { min: 0.2, max: 0.4 },
      size: { min: 5, max: 15 },
      sizeDecay: 20,
      color: ['#ffffff', '#ffff88', '#ffaa44'],
      friction: 3,
    });

    // Smoke
    this.emit({
      position: { x, y },
      count: 15,
      spread: TWO_PI,
      direction: 0,
      speed: { min: radius * 0.5, max: radius * 1.5 },
      life: { min: 0.5, max: 1.0 },
      size: { min: 10, max: 25 },
      sizeDecay: -5, // Grow
      color: ['#333333', '#444444', '#555555'],
      gravity: -50,
      alphaDecay: 1.5,
    });

    // Sparks
    this.emit({
      position: { x, y },
      count: 25,
      spread: TWO_PI,
      direction: 0,
      speed: { min: radius * 2, max: radius * 5 },
      life: { min: 0.3, max: 0.8 },
      size: { min: 2, max: 4 },
      color: ['#ff8844', '#ffaa44', '#ffcc44'],
      gravity: 300,
      friction: 1,
    });
  }

  emitPoison(x: number, y: number): void {
    this.emit({
      position: { x, y },
      count: 12,
      spread: TWO_PI,
      direction: -Math.PI / 2,
      speed: { min: 20, max: 60 },
      life: { min: 0.6, max: 1.2 },
      size: { min: 3, max: 7 },
      sizeDecay: 3,
      color: ['#00cc00', '#44ff00', '#88ff44'],
      gravity: -40,
      alphaDecay: 1.0,
    });
  }

  emitBleed(x: number, y: number): void {
    this.emit({
      position: { x, y },
      count: 8,
      spread: Math.PI / 3,
      direction: Math.PI / 2,
      speed: { min: 30, max: 80 },
      life: { min: 0.4, max: 0.8 },
      size: { min: 2, max: 5 },
      color: ['#cc0000', '#880000', '#ff2222'],
      gravity: 200,
      alphaDecay: 1.5,
    });
  }

  emitHeal(x: number, y: number): void {
    this.emit({
      position: { x, y },
      count: 10,
      spread: Math.PI / 2,
      direction: -Math.PI / 2,
      speed: { min: 40, max: 100 },
      life: { min: 0.6, max: 1.0 },
      size: { min: 3, max: 6 },
      sizeDecay: 2,
      color: ['#00ff88', '#44ffaa', '#88ffcc'],
      gravity: -60,
      alphaDecay: 1.2,
    });
  }

  emitBlock(x: number, y: number): void {
    this.emit({
      position: { x, y },
      count: 8,
      spread: Math.PI,
      direction: -Math.PI / 2,
      speed: { min: 50, max: 120 },
      life: { min: 0.3, max: 0.5 },
      size: { min: 2, max: 4 },
      color: ['#cccccc', '#ffffff', '#aaaaff'],
      gravity: 100,
      friction: 2,
    });
  }

  emitStun(x: number, y: number): void {
    this.emit({
      position: { x, y: y - 20 },
      count: 6,
      spread: TWO_PI,
      direction: 0,
      speed: { min: 15, max: 35 },
      life: { min: 0.8, max: 1.2 },
      size: { min: 4, max: 8 },
      sizeDecay: 3,
      color: ['#ffff00', '#ffcc00', '#ffffff'],
      gravity: -20,
      alphaDecay: 0.8,
    });
  }

  // Floating damage/heal numbers
  addFloatingText(
    x: number,
    y: number,
    text: string,
    color: string,
    critical: boolean = false
  ): void {
    if (this.floatingTexts.length >= this.MAX_TEXTS) {
      // Swap-and-pop oldest (index 0) — O(1) instead of O(n) shift
      this.floatingTexts[0] = this.floatingTexts[this.floatingTexts.length - 1];
      this.floatingTexts.pop();
    }

    this.floatingTexts.push({
      x: x + (Math.random() - 0.5) * 20,
      y,
      vy: -80,
      text,
      color,
      size: critical ? 20 : 14,
      life: 1.0,
      maxLife: 1.0,
      critical,
      spawnTime: performance.now(),
    });
  }

  addDamageNumber(x: number, y: number, damage: number, critical: boolean = false, elementType?: string): void {
    let color = '#ffffff'; // Default white for normal physical damage
    let text = damage.toString();
    let size = 14;

    // Elemental colors
    if (elementType) {
      switch (elementType) {
        case 'od': color = '#ff6600'; break;     // Fire - orange
        case 'su': color = '#00aaff'; break;     // Ice - blue
        case 'gok': color = '#aa88ff'; break;    // Lightning - purple
        case 'karanlik': color = '#cc88ff'; break; // Dark/Chaos - purple
      }
    }

    if (critical) {
      color = '#ffd700'; // Gold for crits
      text = `${damage}!`;
      size = 18;
    }

    // Big damage scale effect (100+)
    const isBigDamage = damage >= 100;
    if (isBigDamage) {
      size = Math.min(24, size + 4);
    }

    if (this.floatingTexts.length >= this.MAX_TEXTS) {
      this.floatingTexts[0] = this.floatingTexts[this.floatingTexts.length - 1];
      this.floatingTexts.pop();
    }

    this.floatingTexts.push({
      x: x + (Math.random() - 0.5) * 20,
      y,
      vy: -80,
      text,
      color,
      size,
      life: 1.0,
      maxLife: 1.0,
      critical,
      bigDamage: isBigDamage,
      spawnTime: performance.now(),
    });
  }

  addHealNumber(x: number, y: number, amount: number): void {
    this.addFloatingText(x, y, `+${amount}`, '#44ff44', false);
  }

  addManaNumber(x: number, y: number, amount: number): void {
    this.addFloatingText(x, y, `+${amount}`, '#4488ff', false);
  }

  addExpNumber(x: number, y: number, amount: number): void {
    this.addFloatingText(x, y, `+${amount} XP`, '#ffaa44', false);
  }

  addMissText(x: number, y: number): void {
    this.addFloatingText(x, y, t('combat.miss'), '#888888', false);
  }

  addBlockText(x: number, y: number): void {
    this.addFloatingText(x, y, t('combat.blocked'), '#4488ff', false);
  }

  /** Inline world→screen to avoid Vector2 allocation per particle */
  private worldToScreenInline(cam: Camera, wx: number, wy: number): void {
    this._sx = (wx - cam.position.x) * cam.zoom;
    this._sy = (wy - cam.position.y) * cam.zoom;
  }

  render(renderer: Renderer, camera: Camera): void {
    const ctx = renderer.context;
    const screenW = renderer.width;
    const screenH = renderer.height;
    const zoom = camera.zoom;

    // Render particles - batch by color to minimize GPU state changes
    ctx.save();

    // Sort active particles by color for batching (only circles/squares on fast path)
    // First, collect visible particles
    const visibleParticles: Particle[] = [];
    for (let i = 0, len = this.particles.length; i < len; i++) {
        const p = this.particles[i];
        this.worldToScreenInline(camera, p.x, p.y);
        if (this._sx < -20 || this._sx > screenW + 20 || this._sy < -20 || this._sy > screenH + 20) continue;
        // Stash screen coords on particle temporarily
        (p as any)._sx = this._sx;
        (p as any)._sy = this._sy;
        visibleParticles.push(p);
    }

    // Sort by color for batching (stable sort since we don't care about depth for particles)
    visibleParticles.sort((a, b) => (a.color < b.color ? -1 : a.color > b.color ? 1 : 0));

    let currentColor = '';
    let currentAlpha = -1;

    for (let i = 0, len = visibleParticles.length; i < len; i++) {
        const p = visibleParticles[i];
        const sx = (p as any)._sx;
        const sy = (p as any)._sy;
        const alpha = Math.max(0, p.alpha);
        const size = p.size * zoom;
        const halfSize = size * 0.5;

        // Fast path: no rotation
        if (p.rotation === 0 && p.rotationSpeed === 0 && (p.shape === 'circle' || p.shape === 'square')) {
            if (p.color !== currentColor) {
                ctx.fillStyle = p.color;
                currentColor = p.color;
            }
            if (alpha !== currentAlpha) {
                ctx.globalAlpha = alpha;
                currentAlpha = alpha;
            }
            if (p.shape === 'circle') {
                ctx.beginPath();
                ctx.arc(sx, sy, halfSize, 0, TWO_PI);
                ctx.fill();
            } else {
                ctx.fillRect(sx - halfSize, sy - halfSize, size, size);
            }
        } else {
            // Slow path: rotated or complex shapes
            ctx.save();
            ctx.globalAlpha = alpha;
            ctx.fillStyle = p.color;
            currentColor = '';
            currentAlpha = -1;
            ctx.translate(sx, sy);
            ctx.rotate(p.rotation);

            switch (p.shape) {
                case 'circle':
                    ctx.beginPath();
                    ctx.arc(0, 0, halfSize, 0, TWO_PI);
                    ctx.fill();
                    break;
                case 'square':
                    ctx.fillRect(-halfSize, -halfSize, size, size);
                    break;
                case 'line':
                    ctx.strokeStyle = p.color;
                    ctx.lineWidth = 2;
                    ctx.beginPath();
                    ctx.moveTo(-halfSize, 0);
                    ctx.lineTo(halfSize, 0);
                    ctx.stroke();
                    break;
                case 'star':
                    this.drawStar(ctx, 0, 0, 5, halfSize, size * 0.25);
                    break;
            }
            ctx.restore();
        }
    }
    ctx.restore();

    // Render floating texts — single save/restore, manual transform resets
    const now = performance.now();
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.lineWidth = 3;

    for (let i = 0, len = this.floatingTexts.length; i < len; i++) {
      const text = this.floatingTexts[i];
      this.worldToScreenInline(camera, text.x, text.y);
      const sx = this._sx, sy = this._sy;

      // Viewport cull
      if (sx < -100 || sx > screenW + 100 || sy < -50 || sy > screenH + 50) continue;

      const lifeRatio = text.life / text.maxLife;
      ctx.globalAlpha = lifeRatio;
      ctx.font = `bold ${text.size * zoom}px Arial`;

      // Scale effect for big damage / critical hits
      let scale = 1.0;
      if (text.bigDamage && text.spawnTime) {
        const elapsed = (now - text.spawnTime) / 1000;
        if (elapsed < 0.2) {
          scale = 1.0 + 0.3 * Math.sin((elapsed / 0.2) * Math.PI);
        }
      } else if (text.critical && text.spawnTime) {
        const elapsed = (now - text.spawnTime) / 1000;
        if (elapsed < 0.15) {
          scale = 1.0 + 0.2 * Math.sin((elapsed / 0.15) * Math.PI);
        }
      }

      if (scale !== 1.0) {
        ctx.save();
        ctx.translate(sx, sy);
        ctx.scale(scale, scale);
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.lineWidth = 3;
        ctx.strokeText(text.text, 0, 0);
        ctx.fillStyle = text.color;
        ctx.fillText(text.text, 0, 0);
        ctx.restore();
      } else {
        ctx.strokeText(text.text, sx, sy);
        ctx.fillStyle = text.color;
        ctx.fillText(text.text, sx, sy);
      }
    }
    ctx.restore();

    // Render chain lines (lightning bolts between enemies)
    if (this.chainLines.length > 0) {
      ctx.save();
      for (const cl of this.chainLines) {
        const alpha = cl.life / cl.maxLife;
        this.worldToScreenInline(camera, cl.x1, cl.y1);
        const sx1 = this._sx, sy1 = this._sy;
        this.worldToScreenInline(camera, cl.x2, cl.y2);
        const sx2 = this._sx, sy2 = this._sy;

        ctx.globalAlpha = alpha * 0.8;
        ctx.strokeStyle = cl.color;
        ctx.lineWidth = 3 * zoom;
        ctx.shadowColor = cl.color;
        ctx.shadowBlur = 8;

        // Jagged lightning line (3 segments)
        ctx.beginPath();
        ctx.moveTo(sx1, sy1);
        const mx = (sx1 + sx2) / 2;
        const my = (sy1 + sy2) / 2;
        const dx = sx2 - sx1;
        const dy = sy2 - sy1;
        const perpX = -dy * 0.15;
        const perpY = dx * 0.15;
        ctx.lineTo(mx + perpX * (0.5 + Math.random()), my + perpY * (0.5 + Math.random()));
        ctx.lineTo(sx2, sy2);
        ctx.stroke();

        // Thin bright core
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1 * zoom;
        ctx.beginPath();
        ctx.moveTo(sx1, sy1);
        ctx.lineTo(mx + perpX * 0.3, my + perpY * 0.3);
        ctx.lineTo(sx2, sy2);
        ctx.stroke();
      }
      ctx.shadowBlur = 0;
      ctx.restore();
    }
  }

  private drawStar(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    spikes: number,
    outerRadius: number,
    innerRadius: number
  ): void {
    let rot = Math.PI / 2 * 3;
    const step = Math.PI / spikes;

    ctx.beginPath();
    ctx.moveTo(cx, cy - outerRadius);

    for (let i = 0; i < spikes; i++) {
      let x = cx + Math.cos(rot) * outerRadius;
      let y = cy + Math.sin(rot) * outerRadius;
      ctx.lineTo(x, y);
      rot += step;

      x = cx + Math.cos(rot) * innerRadius;
      y = cy + Math.sin(rot) * innerRadius;
      ctx.lineTo(x, y);
      rot += step;
    }

    ctx.lineTo(cx, cy - outerRadius);
    ctx.closePath();
    ctx.fill();
  }

  private randomRange(min: number, max: number): number {
    return min + Math.random() * (max - min);
  }

  private lightenColor(color: string, percent: number): string {
    // Simple hex color lightening
    if (!color.startsWith('#')) return color;

    const num = parseInt(color.slice(1), 16);
    const r = Math.min(255, ((num >> 16) & 0xff) + percent);
    const g = Math.min(255, ((num >> 8) & 0xff) + percent);
    const b = Math.min(255, (num & 0xff) + percent);

    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  }

  // ========================
  // Projectile Trail Effect
  // ========================

  emitProjectile(fromX: number, fromY: number, toX: number, toY: number, color: string | string[], trailSize: number = 3): void {
    const dx = toX - fromX;
    const dy = toY - fromY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const steps = Math.max(5, Math.floor(dist / 15));

    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      const px = fromX + dx * t;
      const py = fromY + dy * t;

      let c = color;
      if (Array.isArray(c)) {
        c = c[Math.floor(Math.random() * c.length)];
      }

      if (this.particles.length >= this.MAX_PARTICLES) break;

      this.particles.push({
        x: px + (Math.random() - 0.5) * 6,
        y: py + (Math.random() - 0.5) * 6,
        vx: (Math.random() - 0.5) * 20,
        vy: (Math.random() - 0.5) * 20,
        life: 0.3 + Math.random() * 0.3,
        maxLife: 0.6,
        size: trailSize + Math.random() * 2,
        sizeDecay: 3,
        color: c,
        alpha: 0.8,
        alphaDecay: 2,
        gravity: 0,
        friction: 1,
        rotation: 0,
        rotationSpeed: 0,
        shape: 'circle',
      });
    }
  }

  // ========================
  // Zone Ambient Particles
  // ========================

  emitAmbient(zone: string, cameraX: number, cameraY: number, viewWidth: number = 800, viewHeight: number = 600): void {
    // Only emit if we have room for particles
    if (this.particles.length > this.MAX_PARTICLES - 50) return;

    const halfH = viewHeight / 2;

    switch (zone) {
      case 'otag_koyu':
        // Dust particles + occasional fireflies (yellow dots)
        if (Math.random() < 0.3) {
          this.emit({
            position: {
              x: cameraX + (Math.random() - 0.5) * viewWidth,
              y: cameraY + (Math.random() - 0.5) * viewHeight,
            },
            count: 1,
            spread: TWO_PI,
            direction: -Math.PI / 2,
            speed: { min: 5, max: 15 },
            life: { min: 2, max: 4 },
            size: { min: 1, max: 2 },
            color: ['#d4c5a0', '#c9b896'],
            gravity: -5,
            alphaDecay: 0.3,
          });
        }
        // Fireflies
        if (Math.random() < 0.05) {
          this.emit({
            position: {
              x: cameraX + (Math.random() - 0.5) * viewWidth,
              y: cameraY + (Math.random() - 0.5) * viewHeight,
            },
            count: 1,
            spread: TWO_PI,
            direction: 0,
            speed: { min: 10, max: 30 },
            life: { min: 1.5, max: 3 },
            size: { min: 2, max: 4 },
            color: ['#ffee44', '#ffdd22'],
            gravity: -10,
            alphaDecay: 0.5,
          });
        }
        break;

      case 'karanlik_orman':
        // Green-purple mist + floating leaves
        if (Math.random() < 0.4) {
          this.emit({
            position: {
              x: cameraX + (Math.random() - 0.5) * viewWidth,
              y: cameraY + Math.random() * halfH,
            },
            count: 2,
            spread: TWO_PI,
            direction: Math.PI / 4,
            speed: { min: 5, max: 20 },
            life: { min: 2, max: 5 },
            size: { min: 6, max: 14 },
            sizeDecay: -1,
            color: ['#2a4a2a', '#3a2a4a', '#1a3a1a'],
            gravity: 0,
            alphaDecay: 0.25,
          });
        }
        // Floating leaves
        if (Math.random() < 0.1) {
          this.emit({
            position: {
              x: cameraX + (Math.random() - 0.5) * viewWidth,
              y: cameraY - halfH,
            },
            count: 1,
            spread: Math.PI / 4,
            direction: Math.PI / 2 + 0.3,
            speed: { min: 20, max: 50 },
            life: { min: 3, max: 6 },
            size: { min: 3, max: 5 },
            color: ['#5a3a1a', '#4a4a1a', '#3a2a0a'],
            gravity: 15,
            friction: 0.5,
            rotation: true,
            shape: 'square',
          });
        }
        break;

      case 'ruh_batakligi':
        // Blue-white spirit lights + rising mist
        if (Math.random() < 0.15) {
          this.emit({
            position: {
              x: cameraX + (Math.random() - 0.5) * viewWidth,
              y: cameraY + (Math.random() - 0.5) * viewHeight,
            },
            count: 1,
            spread: TWO_PI,
            direction: 0,
            speed: { min: 8, max: 25 },
            life: { min: 2, max: 4 },
            size: { min: 3, max: 6 },
            color: ['#8888ff', '#aaddff', '#ffffff'],
            gravity: -15,
            alphaDecay: 0.4,
          });
        }
        // Rising mist
        if (Math.random() < 0.35) {
          this.emit({
            position: {
              x: cameraX + (Math.random() - 0.5) * viewWidth,
              y: cameraY + halfH * 0.5,
            },
            count: 2,
            spread: Math.PI / 2,
            direction: -Math.PI / 2,
            speed: { min: 5, max: 15 },
            life: { min: 3, max: 6 },
            size: { min: 10, max: 20 },
            sizeDecay: -2,
            color: ['#667788', '#556677', '#778899'],
            gravity: -8,
            alphaDecay: 0.2,
          });
        }
        break;

      case 'erlik_tapinagi':
        // Red ember particles + falling ash
        if (Math.random() < 0.3) {
          this.emit({
            position: {
              x: cameraX + (Math.random() - 0.5) * viewWidth,
              y: cameraY + (Math.random() - 0.5) * viewHeight,
            },
            count: 1,
            spread: TWO_PI,
            direction: -Math.PI / 2,
            speed: { min: 10, max: 30 },
            life: { min: 1, max: 3 },
            size: { min: 2, max: 4 },
            color: ['#ff4400', '#ff6622', '#ff8844'],
            gravity: -30,
            alphaDecay: 0.5,
          });
        }
        // Falling ash
        if (Math.random() < 0.2) {
          this.emit({
            position: {
              x: cameraX + (Math.random() - 0.5) * viewWidth,
              y: cameraY - halfH,
            },
            count: 1,
            spread: Math.PI / 3,
            direction: Math.PI / 2,
            speed: { min: 10, max: 25 },
            life: { min: 2, max: 5 },
            size: { min: 1, max: 3 },
            color: ['#444444', '#555555', '#666666'],
            gravity: 20,
            friction: 0.3,
            alphaDecay: 0.3,
          });
        }
        break;

      case 'ergenekon_girisi':
        // Mountain wind dust + rock debris
        if (Math.random() < 0.25) {
          this.emit({
            position: { x: cameraX - viewWidth / 2, y: cameraY + (Math.random() - 0.5) * viewHeight },
            count: 2, spread: Math.PI / 4, direction: 0,
            speed: { min: 30, max: 60 }, life: { min: 2, max: 4 },
            size: { min: 1, max: 3 }, color: ['#b0a080', '#9a8a70', '#c0b090'],
            gravity: 5, alphaDecay: 0.3, friction: 0.2,
          });
        }
        if (Math.random() < 0.08) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY - halfH },
            count: 1, spread: Math.PI / 6, direction: Math.PI / 2,
            speed: { min: 15, max: 35 }, life: { min: 1, max: 3 },
            size: { min: 2, max: 4 }, color: ['#887766', '#776655'],
            gravity: 30, friction: 0.4, rotation: true, shape: 'square',
          });
        }
        break;

      case 'firtina_ovasi':
        // Storm wind + lightning sparks
        if (Math.random() < 0.4) {
          this.emit({
            position: { x: cameraX - viewWidth / 2, y: cameraY + (Math.random() - 0.5) * viewHeight },
            count: 3, spread: Math.PI / 6, direction: Math.PI / 8,
            speed: { min: 50, max: 100 }, life: { min: 1, max: 2.5 },
            size: { min: 1, max: 2 }, color: ['#aabbcc', '#99aabb', '#bbccdd'],
            gravity: 3, alphaDecay: 0.4, friction: 0.1,
          });
        }
        if (Math.random() < 0.03) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + (Math.random() - 0.5) * viewHeight },
            count: 3, spread: TWO_PI, direction: 0,
            speed: { min: 40, max: 80 }, life: { min: 0.2, max: 0.5 },
            size: { min: 2, max: 5 }, color: ['#ffffff', '#ccddff', '#aaccff'],
            gravity: 0, alphaDecay: 2.0,
          });
        }
        break;

      case 'ates_magarasi':
        // Fire sparks rising + lava glow
        if (Math.random() < 0.4) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + halfH * 0.5 },
            count: 2, spread: Math.PI / 3, direction: -Math.PI / 2,
            speed: { min: 20, max: 50 }, life: { min: 1, max: 3 },
            size: { min: 1, max: 3 }, color: ['#ff4400', '#ff6600', '#ffaa00'],
            gravity: -40, alphaDecay: 0.5,
          });
        }
        if (Math.random() < 0.15) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + halfH * 0.3 },
            count: 1, spread: TWO_PI, direction: -Math.PI / 2,
            speed: { min: 3, max: 8 }, life: { min: 2, max: 4 },
            size: { min: 8, max: 16 }, color: ['#ff220044', '#ff440033'],
            gravity: -5, alphaDecay: 0.3, sizeDecay: -2,
          });
        }
        break;

      case 'yersub_tahti':
        // Dark purple energy + floating runes
        if (Math.random() < 0.2) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + (Math.random() - 0.5) * viewHeight },
            count: 1, spread: TWO_PI, direction: -Math.PI / 2,
            speed: { min: 10, max: 25 }, life: { min: 2, max: 4 },
            size: { min: 3, max: 6 }, color: ['#9933ff', '#7722cc', '#bb44ff'],
            gravity: -20, alphaDecay: 0.4,
          });
        }
        if (Math.random() < 0.1) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + (Math.random() - 0.5) * viewHeight },
            count: 1, spread: TWO_PI, direction: 0,
            speed: { min: 5, max: 15 }, life: { min: 3, max: 5 },
            size: { min: 2, max: 4 }, color: ['#ddaaff', '#ccbbff'],
            gravity: -10, alphaDecay: 0.3, rotation: true, shape: 'square',
          });
        }
        break;

      case 'karanlik_tunel':
        // Dripping water + cave dust
        if (Math.random() < 0.15) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY - halfH },
            count: 1, spread: 0, direction: Math.PI / 2,
            speed: { min: 40, max: 70 }, life: { min: 0.5, max: 1.5 },
            size: { min: 1, max: 2 }, color: ['#5588aa', '#4477aa'],
            gravity: 60, alphaDecay: 0.5,
          });
        }
        if (Math.random() < 0.2) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + (Math.random() - 0.5) * viewHeight },
            count: 1, spread: TWO_PI, direction: 0,
            speed: { min: 3, max: 10 }, life: { min: 2, max: 4 },
            size: { min: 4, max: 10 }, color: ['#333333', '#2a2a2a', '#3a3a3a'],
            gravity: 0, alphaDecay: 0.25, sizeDecay: -1,
          });
        }
        break;

      case 'olum_nehri':
        // Green toxic mist + floating souls
        if (Math.random() < 0.35) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + halfH * 0.3 },
            count: 2, spread: Math.PI, direction: -Math.PI / 2,
            speed: { min: 5, max: 15 }, life: { min: 3, max: 6 },
            size: { min: 8, max: 16 }, color: ['#224422', '#335533', '#113311'],
            gravity: -5, alphaDecay: 0.2, sizeDecay: -1,
          });
        }
        if (Math.random() < 0.08) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + (Math.random() - 0.5) * viewHeight },
            count: 1, spread: TWO_PI, direction: -Math.PI / 2,
            speed: { min: 10, max: 30 }, life: { min: 2, max: 4 },
            size: { min: 3, max: 5 }, color: ['#88ffaa', '#66dd88', '#aaffcc'],
            gravity: -20, alphaDecay: 0.5,
          });
        }
        break;

      case 'kemik_ormani':
        // Bone dust + ghostly wisps
        if (Math.random() < 0.25) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + halfH * 0.5 },
            count: 2, spread: TWO_PI, direction: -Math.PI / 2,
            speed: { min: 5, max: 15 }, life: { min: 2, max: 5 },
            size: { min: 1, max: 3 }, color: ['#ccbbaa', '#ddccbb', '#bbaa99'],
            gravity: -8, alphaDecay: 0.3,
          });
        }
        if (Math.random() < 0.06) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + (Math.random() - 0.5) * viewHeight },
            count: 1, spread: TWO_PI, direction: 0,
            speed: { min: 15, max: 35 }, life: { min: 1.5, max: 3 },
            size: { min: 4, max: 7 }, color: ['#ffffff88', '#dddddd66'],
            gravity: -15, alphaDecay: 0.6,
          });
        }
        break;

      case 'lanetli_saray':
        // Cursed energy swirls + falling debris
        if (Math.random() < 0.2) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + (Math.random() - 0.5) * viewHeight },
            count: 1, spread: TWO_PI, direction: 0,
            speed: { min: 15, max: 40 }, life: { min: 2, max: 4 },
            size: { min: 3, max: 6 }, color: ['#880044', '#aa0066', '#660033'],
            gravity: 0, alphaDecay: 0.4,
          });
        }
        if (Math.random() < 0.12) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY - halfH },
            count: 1, spread: Math.PI / 4, direction: Math.PI / 2,
            speed: { min: 10, max: 25 }, life: { min: 2, max: 4 },
            size: { min: 1, max: 3 }, color: ['#555544', '#444433', '#666655'],
            gravity: 25, friction: 0.3, rotation: true, shape: 'square',
          });
        }
        break;

      case 'erlik_kapisi':
        // Dark fire + shadow wisps
        if (Math.random() < 0.3) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + halfH * 0.4 },
            count: 2, spread: Math.PI / 3, direction: -Math.PI / 2,
            speed: { min: 15, max: 40 }, life: { min: 1, max: 3 },
            size: { min: 2, max: 4 }, color: ['#cc2200', '#991100', '#ff4422'],
            gravity: -35, alphaDecay: 0.5,
          });
        }
        if (Math.random() < 0.15) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + (Math.random() - 0.5) * viewHeight },
            count: 1, spread: TWO_PI, direction: 0,
            speed: { min: 10, max: 25 }, life: { min: 2, max: 4 },
            size: { min: 5, max: 10 }, color: ['#220011', '#330022', '#110008'],
            gravity: 0, alphaDecay: 0.3, sizeDecay: -1,
          });
        }
        break;

      case 'cehennem_bahcesi':
        // Embers everywhere + toxic fumes
        if (Math.random() < 0.4) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + (Math.random() - 0.5) * viewHeight },
            count: 2, spread: TWO_PI, direction: -Math.PI / 2,
            speed: { min: 10, max: 30 }, life: { min: 1, max: 2.5 },
            size: { min: 1, max: 3 }, color: ['#ff6600', '#ff4400', '#ffaa00', '#ff2200'],
            gravity: -25, alphaDecay: 0.6,
          });
        }
        if (Math.random() < 0.2) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + halfH * 0.3 },
            count: 1, spread: Math.PI / 2, direction: -Math.PI / 2,
            speed: { min: 5, max: 12 }, life: { min: 3, max: 5 },
            size: { min: 10, max: 18 }, color: ['#332200', '#443300', '#221100'],
            gravity: -8, alphaDecay: 0.2, sizeDecay: -2,
          });
        }
        break;

      case 'taht_odasi':
        // Dark energy + blood red particles
        if (Math.random() < 0.25) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + (Math.random() - 0.5) * viewHeight },
            count: 1, spread: TWO_PI, direction: 0,
            speed: { min: 15, max: 35 }, life: { min: 2, max: 4 },
            size: { min: 3, max: 6 }, color: ['#880000', '#aa0000', '#660000'],
            gravity: 0, alphaDecay: 0.4,
          });
        }
        if (Math.random() < 0.1) {
          this.emit({
            position: { x: cameraX + (Math.random() - 0.5) * viewWidth, y: cameraY + halfH * 0.5 },
            count: 2, spread: TWO_PI, direction: -Math.PI / 2,
            speed: { min: 8, max: 20 }, life: { min: 2, max: 5 },
            size: { min: 6, max: 12 }, color: ['#1a0011', '#2a0022', '#0a0008'],
            gravity: -10, alphaDecay: 0.2, sizeDecay: -1,
          });
        }
        break;
    }
  }

  getParticleCount(): number {
    return this.particles.length;
  }

  getTextCount(): number {
    return this.floatingTexts.length;
  }

  clear(): void {
    this.particles = [];
    this.floatingTexts = [];
  }
}

// Global particle system instance
let particleSystemInstance: ParticleSystem | null = null;

export function getParticleSystem(): ParticleSystem {
  if (!particleSystemInstance) {
    particleSystemInstance = new ParticleSystem();
  }
  return particleSystemInstance;
}
